import { Injectable } from '@angular/core';
import {
  AbstractGraphNodeBuilder,
  ConditionDefinition,
  GraphInputDefinition,
  OperatorNodeDefinition,
} from '@fmnts/declarative-forms/core';
import {
  combineLatest,
  concat,
  filter,
  map,
  merge,
  pairwise,
  pluck,
  repeat,
  startWith,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { PredicateNodeBuilder } from './predicate-node-builder.service';

export enum RxJsNodeName {
  /** @see {@link combineLatest} */
  CombineLatest = 'rxjs.combineLatest',
  /** @see {@link concat} */
  Concat = 'rxjs.concat',
  /** @see {@link merge} */
  Merge = 'rxjs.merge',
}

export enum RxJsOperatorNodeName {
  /** @see {@link filter} */
  FilterByCondition = 'filter.by-condition',
  /** @see {@link map} */
  MapToCondition = 'map.to-condition',
  /** @see {@link map} */
  MapTo = 'mapTo',
  /** @see {@link pairwise} */
  Pairwise = 'pairwise',
  /** @see {@link pluck} */
  Pluck = 'pluck',
  /** @see {@link repeatWhen} */
  RepeatWhen = 'repeatWhen',
  /** @see {@link startWith} */
  StartWith = 'startWith',
  /** @see {@link take} */
  Take = 'take',
  /** @see {@link takeUntil} */
  TakeUntil = 'takeUntil',
  /** @see {@link withLatestFrom} */
  WithLatestFrom = 'withLatestFrom',
}

type DefinitionWithOptions = Required<
  Pick<GraphInputDefinition<GraphInputDefinition[]>, 'options'>
>;

type DefinitionWithArguments<T extends any[]> = Required<
  Pick<OperatorNodeDefinition<T>, 'args'>
>;

function getOptionsFromDefinition(
  def: GraphInputDefinition<GraphInputDefinition[]>,
): GraphInputDefinition[] {
  if (!def.options) {
    throw new Error(`Failed to set up Node. '${def.name}' is missing options`);
  }
  return (def as DefinitionWithOptions).options;
}

function getArgsFromDefinition<T extends any[]>(
  def: OperatorNodeDefinition<T>,
): T {
  if (!def.args) {
    throw new Error(
      `Failed to set up Operator. '${def.operator}' is missing arguments`,
    );
  }
  return (def as DefinitionWithArguments<T>).args;
}

/**
 * Builder for rxjs related graph nodes
 */
@Injectable({
  providedIn: 'root',
})
export class RxjsGraphNodeBuilder extends AbstractGraphNodeBuilder<
  RxJsNodeName,
  RxJsOperatorNodeName
> {
  constructor(private pb: PredicateNodeBuilder) {
    super();

    // Add operators
    // TODO: add another argument `addOperator()` in order to validate the options
    // Look into https://github.com/gcanti/io-ts or https://github.com/fabiandev/ts-runtime
    this.addOperator(
      RxJsOperatorNodeName.FilterByCondition,
      (def: OperatorNodeDefinition<[ConditionDefinition]>) => {
        const args = getArgsFromDefinition(def);
        return filter(this.pb.parseCondition(args[0]));
      },
    )

      .addOperator(
        RxJsOperatorNodeName.MapToCondition,
        (def: OperatorNodeDefinition<[ConditionDefinition]>) => {
          const args = getArgsFromDefinition(def);
          return map(this.pb.parseCondition(args[0]));
        },
      )
      .addOperator(
        RxJsOperatorNodeName.MapTo,
        (def: OperatorNodeDefinition<[unknown]>) => {
          const args = getArgsFromDefinition(def);
          return map(() => args[0]);
        },
      )
      .addOperator(RxJsOperatorNodeName.Pairwise, () => pairwise())
      .addOperator(
        RxJsOperatorNodeName.Pluck,
        (def: OperatorNodeDefinition<string[]>) => {
          const args = getArgsFromDefinition(def);
          return pluck(...args);
        },
      )
      .addOperator(
        RxJsOperatorNodeName.RepeatWhen,
        (def: OperatorNodeDefinition<[GraphInputDefinition]>, ctx, root) => {
          const [d] = getArgsFromDefinition(def);
          // Let it fail in case that root.parse doesn't return a value
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return repeat({ delay: () => root.parse(d, ctx)! });
        },
      )
      .addOperator(
        RxJsOperatorNodeName.StartWith,
        (def: OperatorNodeDefinition<unknown[]>) => {
          const args = getArgsFromDefinition(def);
          return startWith(...args);
        },
      )
      .addOperator(
        RxJsOperatorNodeName.Take,
        (def: OperatorNodeDefinition<[{ count: number }]>) => {
          const [{ count }] = getArgsFromDefinition(def);
          return take(count);
        },
      )
      .addOperator(
        RxJsOperatorNodeName.TakeUntil,
        (def: OperatorNodeDefinition<[GraphInputDefinition]>, ctx, root) => {
          const [d] = getArgsFromDefinition(def);
          // Let it fail in case that root.parse doesn't return a value
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return takeUntil(root.parse(d, ctx)!);
        },
      )
      .addOperator(
        RxJsOperatorNodeName.WithLatestFrom,
        (def: OperatorNodeDefinition<GraphInputDefinition[]>, ctx, root) => {
          const args = getArgsFromDefinition(def);
          return withLatestFrom(...args.map((d) => root.parse(d, ctx) ?? []));
        },
      );

    // Add nodes
    this.addNode(
      RxJsNodeName.CombineLatest,
      (def: GraphInputDefinition<GraphInputDefinition[]>, ctx, rootFactory) => {
        const options = getOptionsFromDefinition(def);
        return combineLatest(options.map((d) => rootFactory.parse(d, ctx)));
      },
    )
      .addNode(
        RxJsNodeName.Concat,
        (def: GraphInputDefinition<GraphInputDefinition[]>, ctx, root) => {
          const options = getOptionsFromDefinition(def);
          // Let it fail in case that root.parse doesn't return a value
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return concat(...options.map((d) => root.parse(d, ctx)!));
        },
      )
      .addNode(
        RxJsNodeName.Merge,
        (def: GraphInputDefinition<GraphInputDefinition[]>, ctx, root) => {
          const options = getOptionsFromDefinition(def);
          return merge(...options.map((d) => root.parse(d, ctx)));
        },
      );
  }
}
